import React from 'react';
import { HStack, Stack, Text } from '@chakra-ui/react';
import { ReactComponent as Pending } from 'assets/icons/pendingIcon.svg';
import { ReactComponent as Success } from 'assets/icons/successIcon.svg';
import { useBankAccounts } from 'hooks';
import { useTranslation } from 'localization';

import BorderedBox from '../../../ProfileTab/components/BorderedBox';

const rowBorder = 'rgba(255,255,255,0.06)';
const accountSepBorder = 'rgba(255,255,255,0.08)';

const BankAccounts: React.FC = () => {
  const { data: bankAccounts } = useBankAccounts({ queries: {} });
  const { t } = useTranslation('component.settings.finance');
  const list = bankAccounts?.list ?? [];

  const status = (verified: boolean) =>
    verified ? (
      <HStack gap={2} alignItems="center" flexShrink={0}>
        <Text color="primary" fontSize="sm" fontWeight="medium">
          {t('verified')}
        </Text>
        <Success width={22} />
      </HStack>
    ) : (
      <HStack gap={2} alignItems="center" flexShrink={0}>
        <Text color="warning" fontSize="sm" fontWeight="medium">
          {t('pending')}
        </Text>
        <Pending />
      </HStack>
    );

  return (
    <BorderedBox title={t('savedBankAccounts')} w={{ base: '100%', xl: '50%' }}>
      {list.length === 0 ? (
        <Text fontSize="sm" color="body2" py={2}>
          {t('noBankAccounts')}
        </Text>
      ) : (
        <Stack gap={0}>
          {list.map((account, accIndex) => {
            const detailRows: { key: string; label: string; value: string }[] =
              [];
            if (account.bank_name) {
              detailRows.push({
                key: 'bank',
                label: t('bankName'),
                value: account.bank_name,
              });
            }
            if (account.bank_branch_name) {
              detailRows.push({
                key: 'branch',
                label: t('bankBranchName'),
                value: account.bank_branch_name,
              });
            }
            if (account.account_id) {
              detailRows.push({
                key: 'acc',
                label: t('accName'),
                value: account.account_id,
              });
            }
            if (account.phone) {
              detailRows.push({
                key: 'phone',
                label: t('phone'),
                value: account.phone,
              });
            }
            if (account.holder_name) {
              detailRows.push({
                key: 'holder',
                label: t('accHolderName'),
                value: account.holder_name,
              });
            }

            return (
              <Stack
                key={account.id}
                gap={0}
                pt={accIndex > 0 ? 4 : 0}
                mt={accIndex > 0 ? 4 : 0}
                borderTopWidth={accIndex > 0 ? '1px' : 0}
                borderTopColor={accountSepBorder}
              >
                <HStack
                  w="100%"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={3}
                  py={3}
                  borderBottomWidth={detailRows.length > 0 ? '1px' : 0}
                  borderBottomColor={rowBorder}
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="body"
                    wordBreak="break-word"
                  >
                    {account.title}
                  </Text>
                  {status(account.status === 'VERIFIED')}
                </HStack>
                {detailRows.map((row, rowIndex) => (
                  <Stack
                    key={row.key}
                    alignItems={{ base: 'flex-start', md: 'center' }}
                    justifyContent="flex-start"
                    flexDir={{ base: 'column', md: 'row' }}
                    py={3}
                    gap={{ base: 1, md: 0 }}
                    borderBottomWidth={
                      rowIndex < detailRows.length - 1 ? '1px' : 0
                    }
                    borderBottomColor={rowBorder}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="body2"
                      pr={{ md: 3 }}
                      minW={{ md: '140px' }}
                    >
                      {row.label}
                    </Text>
                    <Text fontSize="sm" color="body" wordBreak="break-word">
                      {row.value}
                    </Text>
                  </Stack>
                ))}
              </Stack>
            );
          })}
        </Stack>
      )}
    </BorderedBox>
  );
};

export default BankAccounts;
